import dayjs from 'dayjs'
import { useFormikContext } from 'formik'
import { get, isEmpty } from 'lodash'
import React, { useState } from 'react'
import { useSelector } from 'react-redux'

import {
  apiEndpoints,
  fetchAllInteractionTopicOptions,
  fetchInteractionTopicOptions,
  fetchSubject
} from 'api'
import BaseEntity from 'app/components/BaseEntity'
import { eventAlert, eventEmitter } from 'app/helpers'

import {
  getMissingTemplateFields,
  INTERACTION_TEMPLATES,
  INTERACTION_TEMPLATE_VALIDATE,
  requiredFollowUpDateProblem
} from './templates'
import { navigationPaths, tableColumnDefaultOption } from 'app/config'
import constants from 'app/constants'
import {
  fetchInteractionHistoryData,
  updateInteractionHistoryData
} from 'app/store/actions/communicationEntity'
import { colors } from 'app/theme/colors'
import { sanitize } from 'app/utils'
import { validateNotEmpty, validateNotNil } from 'app/validators'

import { columns } from './config'

import FollowUpWidget from '../../../components/FollowUpWidget'
import NestedFieldWidget from '../../../components/NestedFieldWIdget'

// The topic is picked on EVERY outcome, the summary / template only on a
// Connected call (user 2026-09-24) — the same rules as the shared
// InteractionFields. BaseForm hands a widget its static column config, so the
// Connected gate has to read the live form here. Module-level, so BaseForm
// doesn't remount it (and drop the editor's contents) on every render.
// eslint-disable-next-line react/prop-types
const InteractionTopicsWidget = ({ config, ...props }) => {
  const { values } = useFormikContext()
  const isConnected = get(values, 'connected_status') === 'Connected'
  const gatedConfig = React.useMemo(
    () =>
      isConnected
        ? config
        : {
            ...config,
            fields: (config?.fields || []).filter((f) => f.name !== 'summary')
          },
    [config, isConnected]
  )
  return <NestedFieldWidget {...props} config={gatedConfig} />
}

const InteractionHistory = () => {
  const interactionHistoryData = useSelector(
    (state) => state.communicationEntityReducer.interactionHistoryData
  )
  const [expandedRows, setExpandedRows] = React.useState({})
  const toggleRows = (rowsId) => {
    setExpandedRows((prevExpandedRows) => ({
      ...prevExpandedRows,
      [rowsId]: !prevExpandedRows[rowsId]
    }))
  }
  const [topicOptions, setTopicOptions] = useState([])
  // Only the CURRENT topics are offered (user 2026-09-24); topicOptions maps
  // every topic ever stored so an old interaction's retired topic still shows.
  const [activeTopics, setActiveTopics] = useState(null)
  React.useEffect(() => {
    const fetchOptions = async () => {
      try {
        const [optionsResponse, allResponse] = await Promise.all([
          fetchInteractionTopicOptions(),
          fetchAllInteractionTopicOptions()
        ])
        const optionsArray = optionsResponse?.results?.[0]?.value || []
        const optionsObject = [
          ...optionsArray,
          ...(allResponse?.results?.[0]?.value || [])
        ].reduce((acc, option) => {
          acc[option] = option
          return acc
        }, {})
        setActiveTopics(optionsArray)
        setTopicOptions(optionsObject)
      } catch (error) {
        console.error('Error fetching options:', error)
      }
    }
    fetchOptions()
  }, [])

  // Subject names for the "Program(s) interested in" multi-select in the
  // New Program Enquiry template.
  const [subjectOptions, setSubjectOptions] = useState([])
  React.useEffect(() => {
    const loadSubjects = async () => {
      try {
        const res = await fetchSubject({ queries: { limit: 600, offset: 0 } })
        const names = (res?.results || [])
          .map((s) => s?.name)
          .filter(Boolean)
        setSubjectOptions(Array.from(new Set(names)).sort())
      } catch (error) {
        console.error('Error fetching subjects:', error)
      }
    }
    loadSubjects()
  }, [])

  const modifiedColumns = React.useMemo(() => {
    // BaseForm renders the modal in column order, so the order here IS the
    // form's order: topic → Connected Status → channel (user 2026-09-24). No
    // form-only ordering exists, so the grid follows: the channel column now
    // sits after the summary. connected_status is display:false in the grid.
    const pick = (name) => columns.find((column) => column.name === name)
    const updatedColumns = [
      ...columns.slice(0, 1),
      {
        name: 'parent',
        label: 'Parent',
        widget: 'autocomplete',
        minCharactersToSearch: 10,
        modalEdit: true,
        viewOnlyInModal: true,
        defaultValue: () =>
          new URLSearchParams(window.location.search).get('parent'),
        validate: validateNotNil,
        getStructuredValues: (data) => {
          const getItemStructure = (item) => {
            if (Object.prototype.hasOwnProperty.call(item, 'user')) {
              const { uuid, first_name, last_name, phone_number } = item?.user

              return {
                uuid: uuid,
                name: `${phone_number} (${first_name} ${last_name})`
              }
            } else {
              return {
                uuid: item.uuid,
                name: item.name
              }
            }
          }
          if (Array.isArray(data) && !isEmpty(data)) {
            return [getItemStructure(data[0])]
          } else if (Object.prototype.hasOwnProperty.call(data, 'user')) {
            return [getItemStructure(data)]
          } else if (Object.prototype.hasOwnProperty.call(data, 'results')) {
            return data.results.map((item) => {
              return getItemStructure(item)
            })
          } else {
            return data.map((item) => {
              return getItemStructure(item)
            })
          }
        },
        getFormDataValue: (data) => {
          const getItemStructure = (item) => {
            if (Object.prototype.hasOwnProperty.call(item, 'user')) {
              return item.user.uuid
            }
          }
          if (Object.prototype.hasOwnProperty.call(data, 'user')) {
            return getItemStructure(data)
          }
        },
        getOptionLabelKey: 'name',
        getOptionSelectedKey: 'uuid',
        resultStructure: {
          keyName: 'parent',
          keyValue: 'uuid'
        },
        apiOptions: {
          method: 'get',
          url: apiEndpoints.parent
        },
        options: {
          ...tableColumnDefaultOption.options,
          filterKey: 'parent_name',
          customBodyRender: (value) => {
            return value ? (
              <span>
                <a
                  href={`${navigationPaths.parentdashboard}?parent=${value?.user.uuid}`}
                >
                  {value?.user.full_name}
                </a>
              </span>
            ) : null
          }
        }
      },
      pick('parent_phone_number'),
      pick('centre'),
      pick('interaction_topics_list'),
      {
        name: 'interaction_topics',
        label: 'interaction summary',
        widget: InteractionTopicsWidget,
        //config contains all fields that are present under interaction_topics
        config: {
          ctaLabel: '+ another topic',
          // When a row's topic matches one of these, the free-text editor is
          // replaced by the structured template (gated — other pages using
          // NestedFieldWidget don't pass `templates`).
          templates: INTERACTION_TEMPLATES,
          subjectOptions,
          fields: [
            {
              name: 'topic',
              label: 'topic',
              type: 'select',
              uniqueOption: true, //config option for each select to have a unique option selected
              validate: validateNotEmpty,
              valueLabelMappings: topicOptions,
              activeValues: activeTopics
            },
            {
              name: 'summary',
              label: 'summary',
              type: 'wysiwyg',
              validate: validateNotEmpty
            }
          ]
        },
        disabledInForm: false,
        modalEdit: true,
        options: {
          ...tableColumnDefaultOption.options,
          disableFilter: true,
          customBodyRender: (value) => {
            return value?.map((int, i) => (
              <>
                <div
                  style={{
                    borderRadius: 100,
                    backgroundColor: '#ffe1d9',
                    color: colors.ohOrange,
                    paddingLeft: 12,
                    paddingRight: 12,
                    paddingTop: 1,
                    paddingBottom: 1,
                    maxWidth: 'fit-content',
                    fontWeight: 700
                  }}
                >
                  {int.topic}
                </div>
                <div
                  className="custom-root"
                  dangerouslySetInnerHTML={{ __html: int.summary }}
                />
                {i === value.length - 1 ? null : <br />}
              </>
            ))
          },
          setCellProps: () => ({
            style: {
              minWidth: '500px',
              maxWidth: '500px',
              whiteSpace: 'normal'
            }
          })
        }
        // No conditionalRender: the topic shows on every outcome; the summary
        // inside it is gated by InteractionTopicsWidget.
      },
      pick('connected_status'),
      pick('interaction_mode'),
      // Manual follow-up: a "Follow-up required?" Yes/No question that, on Yes,
      // makes a date mandatory. Placed after the channel, and shown for DNP
      // too — a DNP call is precisely the one that needs a retry date, so every
      // DNP must either pick a follow-up date or explicitly say none is needed.
      {
        name: 'follow_up_date',
        label: 'Follow-up required?',
        widget: FollowUpWidget,
        modalEdit: true,
        disabledInForm: false,
        conditionalRender: (data) =>
          ['Connected', 'DNP'].includes(get(data, 'connected_status')),
        options: {
          ...tableColumnDefaultOption.options,
          disableFilter: true,
          customBodyRender: (value) =>
            value ? (
              <span>{dayjs(value).format('DD MMM YYYY')}</span>
            ) : (
              <span style={{ color: '#9e9e9e' }}>—</span>
            )
        }
      },
      ...columns.slice(6)
    ]
    return updatedColumns
  }, [interactionHistoryData, expandedRows, topicOptions, activeTopics, subjectOptions])

  // NOTE: BaseTable already forwards any URL query param as a filter on
  // mount, so a ?parent=<user uuid> deep-link (Revenue Goals "Add interaction")
  // filters the grid to that parent — as long as the backend accepts a
  // `parent` filter (added to CrmInteractionFilter). No extra wiring here.
  return (
    <>
      <BaseEntity
        entity={constants.INTERACTION_HISTORY}
        label={constants.INTERACTION_HISTORY_LABEL}
        columns={modifiedColumns}
        data={interactionHistoryData}
        fetchData={fetchInteractionHistoryData}
        updateEntityData={updateInteractionHistoryData}
        // Block the save with a popup when the interaction details are
        // incomplete: (a) every field of a structured TEMPLATE must be filled
        // (Connected only — the summary/template never renders otherwise),
        // (b) the "Follow-up required?" question must be answered, and (c) on
        // Yes, the date must be picked — and (d) a Recharge "Will recharge" of
        // Yes / Undecided rules out "No". The follow-up checks apply to both
        // Connected and DNP — the same statuses the widget renders under — so
        // an interaction is never blocked by a field the team can't even see.
        beforeSaveFunction={(formData, onSave) => {
          // Blocking the save means the modal's SAVE button keeps spinning
          // until it's told otherwise — MODAL_FORM_SAVE_ERROR is what resets
          // it, so every block path below has to go through this. A toast is
          // too easy to miss on a long form, so this pops a dialog the team
          // has to dismiss.
          const block = (message, title) => {
            eventAlert(message, constants.ERROR, title)
            eventEmitter.emit(constants.MODAL_FORM_SAVE_ERROR)
          }
          const connectedStatus = get(formData, 'connected_status')
          if (connectedStatus === 'Connected') {
            const missing = []
            ;(formData.interaction_topics || []).forEach((row) => {
              const template = INTERACTION_TEMPLATES[row?.topic]
              if (!template) return
              missing.push(
                ...getMissingTemplateFields(template, row?.structured_data || {})
              )
            })
            if (!isEmpty(missing)) {
              // Turn the blank fields red in every rendered template, and name
              // the first few in the toast.
              eventEmitter.emit(INTERACTION_TEMPLATE_VALIDATE)
              block(
                `Please fill these mandatory fields before saving: ${missing.join(
                  ', '
                )}.`,
                'Mandatory fields missing'
              )
              return
            }
          }
          // Recharge "Will recharge" = Yes / Undecided needs a real follow-up
          // date (user 2026-09-24) — checked first, as the messages below
          // offer "No", which that answer rules out.
          const followUpDateProblem = requiredFollowUpDateProblem(formData)
          if (followUpDateProblem) {
            block(followUpDateProblem, 'Follow-up date required')
            return
          }
          if (['Connected', 'DNP'].includes(connectedStatus)) {
            const followUp = formData.follow_up_date
            if (followUp === '' || followUp === undefined) {
              block(
                'Please answer whether a follow-up is required.',
                'Mandatory fields missing'
              )
              return
            }
            if (followUp === 'yes') {
              block(
                'Please choose a follow-up date, or select “No” for follow-up.',
                'Mandatory fields missing'
              )
              return
            }
          }
          // A non-Connected outcome now carries its topic (user 2026-09-24)
          // but never rendered a summary, and CrmInteractionSummary.summary is
          // NOT NULL — the backend reads row['summary'] outright. The topic
          // stands in, the Grow & Retain drawer's fallback for a DNP; hidden
          // template answers are dropped. onSave dispatches this same object.
          if (connectedStatus !== 'Connected') {
            formData.interaction_topics = (formData.interaction_topics || [])
              .filter((row) => row?.topic)
              .map((row) => ({
                topic: row.topic,
                summary: row.topic,
                structured_data: null
              }))
          }
          onSave()
        }}
        disableDelete
        disableEditButton
      />
    </>
  )
}

export default InteractionHistory
